
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import {
  appendPI,
  generatePI,
  previewPiCode,
} from "@/api/inquiry";
import { searchByCodeKeyword } from "@/api/proforma";

type PiMode = "new" | "custom" | "append";

@Component({
  name: "GenerateAppendPiDialog",
})
export default class GenerateAppendPiDialog extends Vue {
  @Prop({ type: Boolean, default: false }) private visible!: boolean;
  @Prop({ type: Object, default: () => ({}) }) private row!: Record<string, any>;

  private activeMode: PiMode = "new";
  private booting = false;
  private submitting = false;

  private newPiCode = "";
  private newPiLoading = false;

  private customPrefix = "";
  private customSuffix = "";
  private recentPiCodes: string[] = [];

  private appendPiId: string | number | "" = "";
  private appendPiOptions: Array<{ label: string; value: string | number }> = [];
  private appendSearchLoading = false;

  private noticeText =
    `*注意：追加有如下限制
    1: 必须是相同公司（HQ/JR）。
    2: 必须是相同客户。
    3: PI的“发货状态”为“未发货”或“部分发货”。
    4: SN不能重复
    5: 必须是相同币种`;

  private modes: Array<{ label: string; value: PiMode }> = [
    { label: "新PI号码", value: "new" },
    { label: "自定义PI号码", value: "custom" },
    { label: "追加PI", value: "append" },
  ];

  get dialogVisible() {
    return this.visible;
  }

  set dialogVisible(val: boolean) {
    this.$emit("update:visible", val);
  }

  get customerCode() {
    return String(this.row?.customerCode || "");
  }

  get sellerCode() {
    return String(this.row?.sellerCode || "");
  }

  get fullCustomPiCode() {
    return `${this.customPrefix}${this.customSuffix}`;
  }

  get sellerDisplay() {
    const name = this.row?.sellerName || "";
    if (!this.sellerCode) {
      return name;
    }
    return name ? `${name}(${this.sellerCode})` : `(${this.sellerCode})`;
  }

  get metaItems() {
    return [
      { label: "报价单", value: this.row?.inquirySheetCode },
      { label: "客人编号", value: this.customerCode },
      { label: "客人简称", value: this.row?.customerAbbreviation },
      { label: "销售", value: this.sellerDisplay },
    ];
  }

  @Watch("visible")
  private onVisibleChange(val: boolean) {
    if (val) {
      this.resetState();
      this.bootstrap();
    }
  }

  private resetState() {
    this.activeMode = "new";
    this.submitting = false;
    this.newPiCode = "";
    this.customSuffix = "";
    this.recentPiCodes = [];
    this.appendPiId = "";
    this.appendPiOptions = [];
  }

  private async bootstrap() {
    this.booting = true;
    try {
      await Promise.all([this.loadNewPiCode()]);
    } finally {
      this.booting = false;
    }
  }

  private async loadNewPiCode() {
    if (!this.row?.approvalRecordId) {
      return;
    }
    this.newPiLoading = true;
    try {
      const res: any = await previewPiCode(this.row.approvalRecordId);
      this.customPrefix = res?.prefix || "";
      this.customSuffix = res?.suffix || "";
      this.newPiCode = `${res?.prefix}${res?.suffix}` || "";
      this.recentPiCodes = res?.latestPiCodes || [];
    } catch (e) {
      console.error(e);
      this.recentPiCodes = [];
    } finally {
      this.newPiLoading = false;
    }
  }

  private onCustomSuffixInput(val: string) {
    this.customSuffix = String(val || "")
      .replace(/\D/g, "")
      .slice(0, 4);
  }

  private async searchAppendPi(query: string) {
    if (!query || !this.row?.inquirySheetId) {
      this.appendPiOptions = [];
      return;
    }
    this.appendSearchLoading = true;
    try {
      const res: any = await searchByCodeKeyword({
        piCode: query,
        inquirySheetId: this.row.inquirySheetId,
      });
      this.appendPiOptions = (res || []).map((v: any) => ({
        label: v.code,
        value: v.id,
      }));
    } catch (e) {
      console.error(e);
      this.appendPiOptions = [];
    } finally {
      this.appendSearchLoading = false;
    }
  }

  private handleClosed() {
    this.resetState();
  }

  private async handleSubmit() {
    if (this.activeMode === "new") {
      await this.submitNewPi();
      return;
    }
    if (this.activeMode === "custom") {
      await this.submitCustomPi();
      return;
    }
    await this.submitAppendPi();
  }

  private async submitNewPi() {
    try {
      await this.$confirm("确定使用系统新 PI 号码生成吗？", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      });
    } catch {
      return;
    }
    this.submitting = true;
    try {
      await generatePI(this.row.approvalRecordId, {code: this.newPiCode});
      this.$notify({
        title: "成功",
        message: "生成PI成功",
        type: "success",
        duration: 2000,
      });
      this.dialogVisible = false;
      this.$emit("success");
    } catch (e) {
      console.error(e);
    } finally {
      this.submitting = false;
    }
  }

  private async submitCustomPi() {
    if (this.customSuffix.length !== 4) {
      this.$message.warning("请填写四位数字编号");
      return;
    }
    const piCode = this.fullCustomPiCode;
    try {
      await this.$confirm(`确定生成自定义 PI「${piCode}」吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      });
    } catch {
      return;
    }
    this.submitting = true;
    try {
      await generatePI(this.row.approvalRecordId, {code: piCode});
      this.$notify({
        title: "成功",
        message: "自定义PI生成成功",
        type: "success",
        duration: 2000,
      });
      this.dialogVisible = false;
      this.$emit("success");
    } catch (e) {
      console.error(e);
    } finally {
      this.submitting = false;
    }
  }

  private async submitAppendPi() {
    if (!this.appendPiId) {
      this.$message.warning("请选择目标PI");
      return;
    }
    try {
      await this.$confirm("确定要追加到此销售订单中吗?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      });
    } catch {
      this.$message({ type: "info", message: "已取消" });
      return;
    }
    this.submitting = true;
    try {
      await appendPI({
        approvalRecordId: this.row.approvalRecordId,
        piId: this.appendPiId,
      });
      this.$notify({
        title: "成功",
        message: "追加PI成功",
        type: "success",
        duration: 2000,
      });
      this.dialogVisible = false;
      this.$emit("success");
    } catch (e) {
      console.error(e);
    } finally {
      this.submitting = false;
    }
  }
}
